import {
  camelCaseObjectKeys,
  snakeCaseObjectKeys,
} from '@doordash/object-utils'
import axios from 'axios'
import { get } from 'lodash'
import { GUARANTEED_EARNINGS_WRONG_ZIP_ERROR } from '../constants'

export type ApplicantData = {
  email: string
  phoneNumber: string
  zipCode: string
  guaranteedEarningsCode?: string
}

export type ApplicationData = ApplicantData & {
  referredBy?: string
  source?: string
  referralDomain?: string
  referralCampaign?: string
  signupCountryShortname?: string
}

/**
 * The returned payload for a successful application submission.
 *
 * Note that this type doesn't describe the complete payload.
 * Existing properties returned by the API should be added
 * and maintained here, as needed and discovered.
 *
 * The serializer to compose the response can be found in:
 * https://github.com/doordash/doorstep-django/blob/master/doordash/dasher/api/dasher_applicant/v1/serializers.py
 */
export type ApplicationSubmissionSuccessPayload = {
  /**
   * A url which contains further details for applicants to continue their application
   */
  uniqueLink: string
}

/**
 * Catch the async request error and display the errors back to the users.
 */
export type ApplicationSubmissionFailurePayload = {
  /**
   * Translated strings pertaining to the input field that had the error
   */
  fieldErrors?: {
    email?: string
    phoneNumber?: string
    zipCode?: string
    /**
     * Misc error, that isn't related to a specific field
     */
    detail?: string
  }
}

export const submitDasherApplication = async (
  applicationData: ApplicationData
): Promise<ApplicationSubmissionSuccessPayload> => {
  const url = '/v1/dasher_applicants/'
  try {
    const response = await axios.post(url, snakeCaseObjectKeys(applicationData))
    return camelCaseObjectKeys(response.data)
  } catch (error) {
    const responseData = get(error, 'response.data')
    if (
      responseData &&
      responseData.error === GUARANTEED_EARNINGS_WRONG_ZIP_ERROR
    ) {
      const wrongZipError = { ...error.response.data, applicationData }
      throw wrongZipError
    } else if (responseData) {
      throw {
        fieldErrors: camelCaseObjectKeys(responseData),
      } as ApplicationSubmissionFailurePayload
    }
    throw error
  }
}
